import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DOW, MONTHS, fmtIsoDate, parseIso, toIso, todayIso } from "@/lib/dates";

type Props = {
  value: string; // yyyy-mm-dd or ""
  onChange: (e: { target: { value: string } }) => void;
  min?: string; max?: string; className?: string; required?: boolean; disabled?: boolean; placeholder?: string; id?: string;
  isDisabled?: (iso: string) => boolean; // e.g. closed days: greyed out and not typeable
};

/** dd/mm/yyyy text field + calendar popover, ported from the quote platform's date-range picker (Monday-first
 * grid, month/year selects, Prev / Today / Next). Emits ISO dates like a native input would. */
export function DateInput({ value, onChange, min, max, className = "", required, disabled, placeholder, id, isDisabled }: Props) {
  const { i18n } = useTranslation();
  const lang = i18n.language === "el" ? "el" : "en";
  const [text, setText] = useState(fmtIsoDate(value));
  const [open, setOpen] = useState(false);
  const base = parseIso(value) ?? new Date();
  const [view, setView] = useState({ y: base.getFullYear(), m: base.getMonth() });
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => { setText(fmtIsoDate(value)); const d = parseIso(value); if (d) setView({ y: d.getFullYear(), m: d.getMonth() }); }, [value]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const disabledIso = (iso: string) => (!!min && iso < min) || (!!max && iso > max) || (isDisabled?.(iso) ?? false);
  const emit = (iso: string) => { onChange({ target: { value: iso } }); setOpen(false); };
  const onText = (raw: string) => {
    let v = raw.replace(/[^\d]/g, "").slice(0, 8);
    if (v.length >= 5) v = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`;
    else if (v.length >= 3) v = `${v.slice(0, 2)}/${v.slice(2)}`;
    setText(v);
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
    if (m) { const iso = `${m[3]}-${m[2]}-${m[1]}`; if (parseIso(iso) && !disabledIso(iso)) onChange({ target: { value: iso } }); }
    if (v === "") onChange({ target: { value: "" } });
  };
  const first = new Date(view.y, view.m, 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const prevDays = new Date(view.y, view.m, 0).getDate();
  const cells: { iso: string; day: number; out: boolean }[] = [];
  for (let i = 0; i < lead; i++) cells.push({ iso: toIso(new Date(view.y, view.m - 1, prevDays - lead + 1 + i)), day: prevDays - lead + 1 + i, out: true });
  for (let d = 1; d <= days; d++) cells.push({ iso: toIso(new Date(view.y, view.m, d)), day: d, out: false });
  while (cells.length % 7) { const n = cells.length - lead - days + 1; cells.push({ iso: toIso(new Date(view.y, view.m + 1, n)), day: n, out: true }); }
  const today = todayIso();
  const years = Array.from({ length: 12 }, (_, i) => view.y - 5 + i);
  return (
    <div ref={wrap} className={`relative ${className}`}>
      <div className="flex items-center rounded-lg border bg-white focus-within:ring-2 focus-within:ring-brand/40">
        <input id={id} className="w-full rounded-lg px-3 py-1.5 text-sm outline-none" value={text} onChange={(e) => onText(e.target.value)} placeholder={placeholder ?? "ηη/μμ/εεεε"} inputMode="numeric" required={required} disabled={disabled} onFocus={() => !disabled && setOpen(true)} />
        <button type="button" className="px-2 text-gray-500 hover:text-gray-800" disabled={disabled} onClick={() => setOpen(!open)} aria-label="calendar">📅</button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-64 rounded-lg border bg-white p-2 shadow-xl text-xs">
          <div className="mb-1 flex gap-1">
            <button type="button" className="flex-1 rounded border bg-gray-50 px-1 py-0.5 hover:bg-gray-100" onClick={() => setView(view.m === 0 ? { y: view.y - 1, m: 11 } : { y: view.y, m: view.m - 1 })}>‹</button>
            <button type="button" className="flex-1 rounded border bg-gray-50 px-1 py-0.5 hover:bg-gray-100" onClick={() => { const d = new Date(); setView({ y: d.getFullYear(), m: d.getMonth() }); if (!disabledIso(today)) emit(today); }}>{lang === "el" ? "Σήμερα" : "Today"}</button>
            <button type="button" className="flex-1 rounded border bg-gray-50 px-1 py-0.5 hover:bg-gray-100" onClick={() => setView(view.m === 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m: view.m + 1 })}>›</button>
          </div>
          <div className="mb-1 flex gap-1">
            <select className="flex-1 rounded border px-1 py-0.5" value={view.m} onChange={(e) => setView({ ...view, m: Number(e.target.value) })}>{MONTHS[lang].map((n, i) => <option key={i} value={i}>{n}</option>)}</select>
            <select className="w-20 rounded border px-1 py-0.5" value={view.y} onChange={(e) => setView({ ...view, y: Number(e.target.value) })}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
          </div>
          <div className="grid grid-cols-7 gap-px">
            {DOW[lang].map((d) => <div key={d} className="py-0.5 text-center text-[10px] font-semibold uppercase text-gray-500">{d}</div>)}
            {cells.map((c, i) => {
              const off = c.out || disabledIso(c.iso);
              const sel = c.iso === value;
              return (
                <button type="button" key={i} disabled={off} onClick={() => emit(c.iso)}
                  className={`py-1 text-center ${off ? "text-gray-300" : sel ? "rounded bg-brand font-bold text-white" : "rounded hover:bg-brand/10"} ${c.iso === today && !sel ? "ring-1 ring-amber-400" : ""}`}>{c.day}</button>
              );
            })}
          </div>
          {value && <button type="button" className="mt-1 w-full rounded border px-1 py-0.5 text-gray-600 hover:bg-gray-50" onClick={() => { onChange({ target: { value: "" } }); setText(""); setOpen(false); }}>{lang === "el" ? "Καθαρισμός" : "Clear"}</button>}
        </div>
      )}
    </div>
  );
}
